import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "./ImageUpload";
import { Globe, Share2, Search, ChevronDown, ChevronUp, Save, CheckCircle2 } from "lucide-react";

const FIELD_LABELS = {
  title: "Page Title",
  meta_description: "Meta Description",
  og_title: "OG Title (Social Share)",
  og_description: "OG Description (Social Share)",
  og_image: "OG Image (Social Share Preview)",
  keywords: "Keywords",
};

const EMPTY = { title: "", meta_description: "", og_title: "", og_description: "", og_image: "", keywords: "" };

export default function AdminSEO() {
  const [pages, setPages] = useState([]);
  const [settings, setSettings] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [forms, setForms] = useState({});
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await http.get("/seo/admin/all");
      setPages(data.pages);
      setSettings(data.settings);
      const f = {};
      data.pages.forEach(p => {
        f[p.key] = data.settings[p.key] || { ...EMPTY, page: p.key };
      });
      setForms(f);
    } catch { toast.error("Failed to load SEO settings"); }
    finally { setLoading(false); }
  };

  const save = async (pageKey) => {
    setSaving(pageKey);
    try {
      await http.put(`/seo/admin/${pageKey}`, forms[pageKey]);
      toast.success(`SEO updated for ${pages.find(p => p.key === pageKey)?.label}`);
      await load();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(null); }
  };

  const updateField = (pageKey, field, value) => {
    setForms(prev => ({ ...prev, [pageKey]: { ...prev[pageKey], [field]: value } }));
  };

  const hasContent = (pageKey) => {
    const s = settings[pageKey];
    if (!s) return false;
    return !!(s.title || s.meta_description || s.og_title || s.og_description || s.og_image || s.keywords);
  };

  if (loading) return <div className="text-center py-12 text-zinc-500 font-mc-b text-sm animate-pulse">Loading SEO settings...</div>;

  return (
    <div className="space-y-6" data-testid="admin-seo">
      <div className="flex items-center gap-3 mb-2">
        <Globe size={20} className="text-[#C9A055]" />
        <div>
          <h2 className="text-[#F4F1EA] font-display text-xl">SEO & Social Media</h2>
          <p className="text-zinc-500 text-xs font-ui">Control how each page appears in search results and social media shares</p>
        </div>
      </div>

      <div className="space-y-3">
        {pages.map(page => {
          const isOpen = expanded === page.key;
          const form = forms[page.key] || EMPTY;
          const configured = hasContent(page.key);

          return (
            <div key={page.key} className="border border-white/10 rounded-xl bg-[#0a0a0a] overflow-hidden" data-testid={`seo-page-${page.key}`}>
              {/* Header */}
              <button
                onClick={() => setExpanded(isOpen ? null : page.key)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors"
                data-testid={`seo-toggle-${page.key}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${configured ? "bg-emerald-500" : "bg-zinc-600"}`} />
                  <span className="font-ui text-sm text-[#F4F1EA] tracking-wide">{page.label}</span>
                  {configured && <span className="text-[9px] tracking-wider uppercase text-emerald-500/70 font-ui">Configured</span>}
                </div>
                {isOpen ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
              </button>

              {/* Expanded Editor */}
              {isOpen && (
                <div className="border-t border-white/[0.06] px-5 py-6 space-y-8">
                  {/* Search Engine Preview */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Search size={14} className="text-zinc-500" />
                      <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-ui">Google Preview</span>
                    </div>
                    <div className="bg-white rounded-lg p-4 max-w-xl">
                      <div className="text-[#1a0dab] text-lg font-sans leading-snug truncate">
                        {form.title || `${page.label} — Simple Truths Club`}
                      </div>
                      <div className="text-[#006621] text-sm font-sans truncate mt-0.5">
                        simpletruthsclub.com/{page.key === "home" ? "" : page.key}
                      </div>
                      <div className="text-[#545454] text-sm font-sans mt-1 line-clamp-2">
                        {form.meta_description || "No description set. Add a meta description to control what appears here."}
                      </div>
                    </div>
                  </div>

                  {/* Social Share Preview */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Share2 size={14} className="text-zinc-500" />
                      <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-ui">Social Share Preview</span>
                    </div>
                    <div className="bg-[#18191A] rounded-lg overflow-hidden max-w-md border border-white/10">
                      {form.og_image ? (
                        <img
                          src={form.og_image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.og_image}` : form.og_image}
                          alt="" className="w-full h-48 object-cover"
                        />
                      ) : (
                        <div className="w-full h-32 bg-zinc-800 flex items-center justify-center text-zinc-600 text-xs">No OG image set</div>
                      )}
                      <div className="p-3">
                        <div className="text-[10px] text-zinc-500 uppercase tracking-wider">simpletruthsclub.com</div>
                        <div className="text-[#E4E6EB] text-sm font-medium mt-1 truncate">
                          {form.og_title || form.title || page.label}
                        </div>
                        <div className="text-zinc-500 text-xs mt-0.5 line-clamp-2">
                          {form.og_description || form.meta_description || "No description set"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Fields */}
                  <div className="grid gap-5">
                    <div>
                      <label className="text-zinc-400 text-[10px] uppercase tracking-wider block mb-1.5">{FIELD_LABELS.title}</label>
                      <input
                        value={form.title}
                        onChange={e => updateField(page.key, "title", e.target.value)}
                        placeholder={`${page.label} — Simple Truths Club`}
                        className="w-full bg-transparent border border-white/10 text-[#F4F1EA] px-4 py-3 rounded-lg text-sm focus:border-[#C9A055] focus:outline-none transition-colors font-ui"
                        data-testid={`seo-title-${page.key}`}
                      />
                      <div className="text-zinc-600 text-[10px] mt-1 font-ui">{form.title.length}/60 characters (recommended)</div>
                    </div>

                    <div>
                      <label className="text-zinc-400 text-[10px] uppercase tracking-wider block mb-1.5">{FIELD_LABELS.meta_description}</label>
                      <textarea
                        rows={3}
                        value={form.meta_description}
                        onChange={e => updateField(page.key, "meta_description", e.target.value)}
                        placeholder="Describe this page for search engines..."
                        className="w-full bg-transparent border border-white/10 text-[#F4F1EA] px-4 py-3 rounded-lg text-sm focus:border-[#C9A055] focus:outline-none transition-colors resize-none font-ui"
                        data-testid={`seo-desc-${page.key}`}
                      />
                      <div className="text-zinc-600 text-[10px] mt-1 font-ui">{form.meta_description.length}/160 characters (recommended)</div>
                    </div>

                    <div className="border-t border-white/[0.06] pt-5">
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[#C9A055]/70 font-ui">Open Graph (Social)</span>
                    </div>

                    <div>
                      <label className="text-zinc-400 text-[10px] uppercase tracking-wider block mb-1.5">{FIELD_LABELS.og_title}</label>
                      <input
                        value={form.og_title}
                        onChange={e => updateField(page.key, "og_title", e.target.value)}
                        placeholder={form.title || "Falls back to page title"}
                        className="w-full bg-transparent border border-white/10 text-[#F4F1EA] px-4 py-3 rounded-lg text-sm focus:border-[#C9A055] focus:outline-none transition-colors font-ui"
                        data-testid={`seo-og-title-${page.key}`}
                      />
                    </div>

                    <div>
                      <label className="text-zinc-400 text-[10px] uppercase tracking-wider block mb-1.5">{FIELD_LABELS.og_description}</label>
                      <textarea
                        rows={2}
                        value={form.og_description}
                        onChange={e => updateField(page.key, "og_description", e.target.value)}
                        placeholder={form.meta_description || "Falls back to meta description"}
                        className="w-full bg-transparent border border-white/10 text-[#F4F1EA] px-4 py-3 rounded-lg text-sm focus:border-[#C9A055] focus:outline-none transition-colors resize-none font-ui"
                        data-testid={`seo-og-desc-${page.key}`}
                      />
                    </div>

                    <ImageUpload
                      value={form.og_image}
                      onChange={v => updateField(page.key, "og_image", v)}
                      folder="seo"
                      label={FIELD_LABELS.og_image}
                    />
                    <p className="text-zinc-600 text-[10px] font-ui -mt-4">Recommended: 1200x630px for best results on Facebook, Twitter & LinkedIn</p>

                    <div>
                      <label className="text-zinc-400 text-[10px] uppercase tracking-wider block mb-1.5">{FIELD_LABELS.keywords}</label>
                      <input
                        value={form.keywords}
                        onChange={e => updateField(page.key, "keywords", e.target.value)}
                        placeholder="keyword1, keyword2, keyword3"
                        className="w-full bg-transparent border border-white/10 text-[#F4F1EA] px-4 py-3 rounded-lg text-sm focus:border-[#C9A055] focus:outline-none transition-colors font-ui"
                        data-testid={`seo-keywords-${page.key}`}
                      />
                    </div>
                  </div>

                  {/* Save */}
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={() => save(page.key)}
                      disabled={saving === page.key}
                      className="flex items-center gap-2 bg-[#C9A055] text-black px-6 py-2.5 rounded-lg font-ui text-xs tracking-wider uppercase hover:bg-[#d4af37] transition-colors disabled:opacity-50"
                      data-testid={`seo-save-${page.key}`}
                    >
                      {saving === page.key ? (
                        <><div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" /> Saving...</>
                      ) : (
                        <><Save size={14} /> Save SEO Settings</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
